/**
 * 計画マスタ 所属発電所 編集 UI (発電販売計画マスタ専用)。
 *
 * gen-sales-plant-master-select 設計書: 発電所マスタからのドロップダウン選択に
 * 置換 (旧 free-text `plant_code` 入力を廃止)。 select 本体は DS `Select`
 * (= `~/components/common/FormSelect`、 2026-08-05 デザイナー確定)。
 *
 * 各行 = 発電所 select + 有効期間(validFrom/validTo)。
 * 候補 filter = 同一エリア(areaCode === selfAreaCode、AC-03) + 既選択除外
 * (AC-04)。 PlanMasterBgsSection と同じ select-with-defensive-retention
 * パターン (availableOptions) を踏襲する。
 */
import { useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type Control,
  type FieldValues,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { iconSize } from '~/constants/iconSize'
import { todayCivilDate } from '~/models/civilDate'
import type { PlantMasterListItemRow } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useListPlantMasters'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import { availableOptions } from '~/pages/AdminOcctoMaster/tabs/_common/models/availableOptions'
import { planMasterRowDefaultValidFrom } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type PlantEntry = {
  plantMasterId: string
  validFrom: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /** i18n ページキー namespace */
  i18nNs: 'adminOcctoMaster.generationSalesPlanMaster'
  /**
   * 発電所マスタ候補一覧 (GetGenerationSalesPlanMasterFormOptions.plants、
   * AC-05: 区分フィルタなしの全件)。
   */
  plantMasters: PlantMasterListItemRow[]
  /** 計画提出者のエリアコード (同一エリアの発電所のみ候補に絞り込む、AC-03) */
  selfAreaCode: AreaCode | undefined
}

export const PlanMasterPlantsSection = ({
  control,
  i18nNs,
  plantMasters,
  selfAreaCode,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'plants',
  })

  const rawWatchedPlants = useWatch({ control, name: 'plants' }) as
    | PlantEntry[]
    | undefined
  const watchedPlants = rawWatchedPlants ?? []
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' }) as
    | { year: number; month: number; day: number }
    | undefined

  // 行ごとの select は FormSelect が内部で Controller を張るため、 error 表示用の
  // fieldState はここで一括購読する (行ごとに Controller を二重に張らない)。
  const { errors } = useFormState({ control })
  const plantErrors = errors.plants as
    | Array<{ plantMasterId?: { message?: string } } | undefined>
    | undefined

  /**
   * area narrow 済み候補 (全行共通、AC-03)。
   */
  const filteredCandidates = useMemo(
    () =>
      plantMasters.filter(
        (p) => selfAreaCode === undefined || p.areaCode === selfAreaCode,
      ),
    [plantMasters, selfAreaCode],
  )

  const noCandidates = filteredCandidates.length === 0

  return (
    <Box>
      {/* セクションヘッダー: ラベル + 追加ボタン (design `.occto-sub-header` 準拠:
          border-top + padding-top + marginTop で直前セクションと区切り、
          add ボタンは size="sm" で他マスタと統一)。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t(`page:${i18nNs}.form.plants`)}
        </Text>
        <ButtonTooltip
          content={
            noCandidates ? t(`page:${i18nNs}.form.noPlantCandidates`) : ''
          }
        >
          <TertiaryButton
            disabled={noCandidates}
            onClick={() => {
              void append({
                plantMasterId: '',
                validFrom: planMasterRowDefaultValidFrom(
                  watchedRecordValidFrom,
                ),
                validTo: undefined,
              })
            }}
            size="sm"
            title={
              noCandidates ? t(`page:${i18nNs}.form.noPlantCandidates`) : ''
            }
          >
            {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                揃わず、 翻訳のたびに記号を書き写す必要もあった。
                design css `.btn:not(...)>svg` = 20px。 */}
            <FaPlusRegular boxSize={iconSize.labeledButton} />
            {t(`page:${i18nNs}.form.addPlant`)}
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>

      {/* 発電所エントリ一覧 */}
      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="md">
            {t(`page:${i18nNs}.form.noPlantsRegistered`)}
          </Text>
        )}
        {fields.map((field, idx) => {
          const entry: PlantEntry = watchedPlants[idx] ?? {
            plantMasterId: '',
            validFrom: todayCivilDate(),
          }

          /**
           * この行の plantMasterId を除いた使用済み ID セット (AC-04)。
           * 行ごとに都度計算して「既選択 ID がリストに残る」を保証する。
           */
          const usedIdsExcludingThisRow = new Set(
            watchedPlants
              .filter((_, i) => i !== idx)
              .map((p) => p.plantMasterId)
              .filter((id) => id !== ''),
          )

          const plantOptsForRow = availableOptions(
            filteredCandidates,
            usedIdsExcludingThisRow,
            entry.plantMasterId,
          )

          const plantError = plantErrors?.[idx]?.plantMasterId

          return (
            <Box
              key={field.id}
              padding={3}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
            >
              <Stack gap={3}>
                {/* 発電所 select + 削除ボタン */}
                <HStack align="flex-end" gap={3}>
                  <Box flex={1}>
                    <FieldRoot invalid={plantError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.plantLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        aria-label={t(`page:${i18nNs}.form.plantLabel`)}
                        control={control}
                        // 既選択値が opts に無い場合のフォールバック (表示のみ。
                        // 選択肢には出さない)。 未選択 ('') は placeholder に戻す。
                        fallbackLabel={(v) => (v === '' ? undefined : v)}
                        name={`plants.${idx}.plantMasterId`}
                        options={plantOptsForRow.map((p) => ({
                          value: p.id,
                          label: `${p.plantCode} - ${p.plantName}`,
                        }))}
                        placeholder={t(`page:${i18nNs}.form.plantUnselected`)}
                      />
                      {plantError !== undefined && (
                        <FieldErrorText>{plantError.message}</FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  <TertiaryButton
                    flexShrink={0}
                    aria-label={t(`page:${i18nNs}.form.removePlant`)}
                    colorPalette="red"
                    onClick={() => {
                      remove(idx)
                    }}
                    size="sm"
                  >
                    {t(`page:${i18nNs}.form.removePlant`)}
                  </TertiaryButton>
                </HStack>

                {/* 適用期間 */}
                <PeriodFields
                  basePath={`plants.${idx}`}
                  clearAsNull
                  control={control}
                  i18nNs={i18nNs}
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}
